import type { Role } from "gsintegrations";
import { axiosClient } from "../api/axiosClient";

/**
 * Role form values for create/update operations
 */
export interface RoleFormData {
  name: string;
  description?: string;
  permissionCodes: string[];
}

/**
 * Role Repository
 *
 * Handles all role-related API calls with permission management
 */
export const roleRepository = {
  /**
   * Get system-level roles for onboarding (no terreiro context)
   */
  async getOnboardingRoles(): Promise<Role[]> {
    const response = await axiosClient.get<Role[]>("/roles", {
      withCredentials: true,
    });
    return response.data;
  },

  /**
   * Get all roles for current terreiro
   */
  async getAll(terreiroId: string): Promise<Role[]> {
    const response = await axiosClient.get<Role[]>(
      `/terreiros/${terreiroId}/roles`,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },

  /**
   * Get role by ID
   */
  async getById(roleId: string, terreiroId?: string): Promise<Role> {
    const response = await axiosClient.get<Role>(`/roles/${roleId}`, {
      withCredentials: true,
      params: terreiroId ? { terreiroId } : undefined,
    });
    return response.data;
  },

  /**
   * Create new role
   */
  async create(terreiroId: string, data: RoleFormData): Promise<Role> {
    const response = await axiosClient.post<Role>(
      `/terreiros/${terreiroId}/roles`,
      data,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },

  /**
   * Update existing role
   */
  async update(
    roleId: string,
    data: RoleFormData,
    terreiroId?: string,
  ): Promise<Role> {
    const response = await axiosClient.put<Role>(
      `/roles/${roleId}`,
      {
        ...data,
        terreiroId,
      },
      {
        withCredentials: true,
      },
    );
    return response.data;
  },

  /**
   * Delete role
   */
  async delete(roleId: string, terreiroId?: string): Promise<void> {
    await axiosClient.delete(`/roles/${roleId}`, {
      withCredentials: true,
      params: terreiroId ? { terreiroId } : undefined,
    });
  },

  /**
   * Get permissions for a specific role
   */
  async getPermissions(roleId: string, terreiroId?: string): Promise<string[]> {
    const response = await axiosClient.get<string[]>(
      `/roles/${roleId}/permissions`,
      {
        withCredentials: true,
        params: terreiroId ? { terreiroId } : undefined,
      },
    );
    return response.data;
  },

  /**
   * Replace all permissions for a role
   */
  async replacePermissions(
    roleId: string,
    permissionCodes: string[],
    terreiroId?: string,
  ): Promise<void> {
    await axiosClient.put(
      `/roles/${roleId}/permissions`,
      { permissionCodes, terreiroId },
      {
        withCredentials: true,
      },
    );
  },

  /**
   * Add permissions to a role
   */
  async addPermissions(
    roleId: string,
    permissionCodes: string[],
  ): Promise<void> {
    await axiosClient.post(
      `/roles/${roleId}/permissions`,
      { permissionCodes },
      {
        withCredentials: true,
      },
    );
  },

  /**
   * Remove permission from role
   */
  async removePermission(
    roleId: string,
    permissionCode: string,
  ): Promise<void> {
    await axiosClient.delete(`/roles/${roleId}/permissions/${permissionCode}`, {
      withCredentials: true,
    });
  },
};
